import { prefersReducedMotion } from '@/effects/motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

type RevealOptions = {
  selector?: string;
  stagger?: number;
};

export const initReveal = (options: RevealOptions = {}): (() => void) => {
  const selector = options.selector ?? 'section > *, .card, .team-member, .reveal-text';

  if (prefersReducedMotion()) return () => {};

  const elements = document.querySelectorAll<HTMLElement>(selector);

  // Determine per-element enter animation based on optional data/class hints
  const getFromProps = (el: HTMLElement): gsap.TweenVars => {
    if (el.matches('.tilt-card, .swiper-slide')) {
      return { y: 35, opacity: 0, scale: 0.94, filter: 'blur(6px)' };
    }
    if (el.matches('.reveal-left')) {
      return { x: -60, opacity: 0, filter: 'blur(4px)' };
    }
    if (el.matches('.reveal-right')) {
      return { x: 60, opacity: 0, filter: 'blur(4px)' };
    }
    return { y: 48, opacity: 0, filter: 'blur(5px)' };
  };

  elements.forEach(el => {
    const from = getFromProps(el);
    gsap.set(el, { ...from, visibility: 'hidden' });
  });

  ScrollTrigger.batch(elements, {
    onEnter: batch => {
      gsap.to(batch, {
        x: 0,
        y: 0,
        opacity: 1,
        scale: 1,
        filter: 'blur(0px)',
        visibility: 'visible',
        stagger: 0.13,
        duration: 1.05,
        ease: 'power3.out',
        overwrite: true,
      });
    },
    start: 'top 88%',
    once: true,
  });

  // Section titles: extra flair
  const titles = document.querySelectorAll<HTMLElement>('.section-title');
  titles.forEach(el => {
    gsap.set(el, { opacity: 0, y: 30, visibility: 'hidden' });
    ScrollTrigger.create({
      trigger: el,
      start: 'top 85%',
      once: true,
      onEnter: () => {
        gsap.to(el, {
          opacity: 1,
          y: 0,
          visibility: 'visible',
          duration: 1.0,
          ease: 'power3.out',
        });
      },
    });
  });

  return () => {
    ScrollTrigger.getAll().forEach(t => t.kill());
  };
};
